import { ChangeDetectionStrategy, Component, DOCUMENT, inject, OnInit } from '@angular/core'
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router'

import { TranslateService } from '@ngx-translate/core'
import { combineLatest } from 'rxjs'
import { filter, map, startWith, takeUntil, tap } from 'rxjs/operators'

import { COMPANY_INFO_DATA_TAB_NAME } from '~/core/constants'
import { DestroyDirective } from '~/core/directives'
import { CompanyFacade } from '~/core/features'
import { ECompanyInfoRoute, IForecastDto } from '~/core/models'
import { SeoPage, SeoService } from '~/core/services'

import { CompanyBreadcrumbsComponent } from './components/company-breadcrumbs'
import { COMPANY_PAGE_SEO, getCompanyBreadcrumbs } from './constants'

@Component({
  selector: 'app-company',
  templateUrl: './company.component.html',
  styleUrl: './company.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [DestroyDirective],
  imports: [CompanyBreadcrumbsComponent, RouterOutlet],
})
export class CompanyComponent implements OnInit {
  private readonly document = inject<Document>(DOCUMENT)
  private readonly seoService = inject(SeoService)
  private readonly router = inject(Router)
  private readonly translate = inject(TranslateService)

  private readonly companyFacade = inject(CompanyFacade)
  private readonly destroy$ = inject(DestroyDirective).destroy$

  forecast?: IForecastDto | null

  constructor() {}

  ngOnInit(): void {
    combineLatest([
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null)
      ),
      this.companyFacade.forecast$,
      this.companyFacade.ticker$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([, forecast, ticker]) => ({ forecast, ticker })),
        filter(({ ticker }) => Boolean(ticker)),
        tap(({ forecast, ticker }) => {
          this.forecast = forecast
          this.seoService.applyRouteMetadata(
            forecast
              ? {
                  ...this.getCompanyPageSeo(forecast),
                  translationParams: {
                    name: forecast.company.name,
                    ticker: forecast.company.ticker,
                  },
                }
              : {
                  titleKey: 'seo.tickerNotFound.title',
                  descriptionKey: 'seo.tickerNotFound.description',
                  translationParams: { ticker: ticker! },
                  robots: 'noindex,follow',
                  canonicalPath: null,
                }
          )
        })
      )
      .subscribe()
  }

  private getCompanyPageSeo(forecast: IForecastDto): Omit<SeoPage, 'translationParams'> {
    const companyInfoRoute = this.getCompanyInfoRoute()
    const pageSeo = COMPANY_PAGE_SEO[companyInfoRoute] ?? COMPANY_PAGE_SEO[ECompanyInfoRoute.EARN]!
    const jsonLdBreadcrumbs = getCompanyBreadcrumbs(forecast, companyInfoRoute, key =>
      this.translate.instant(key)
    ).map(({ label, path }) => ({ name: label, path }))

    return companyInfoRoute === ECompanyInfoRoute.EDGAR_REPORT
      ? {
          ...pageSeo,
          jsonLdHeadline: this.getReportHeadline(forecast.summary),
          jsonLdPublishedDate: this.getIsoDate(forecast.reportPublishedAt),
          jsonLdModifiedDate: this.getIsoDate(forecast.reportPublishedAt),
          jsonLdBreadcrumbs,
        }
      : { ...pageSeo, jsonLdBreadcrumbs }
  }

  private getCompanyInfoRoute(): ECompanyInfoRoute {
    let activeRoute: ActivatedRouteSnapshot = this.router.routerState.snapshot.root

    while (activeRoute.firstChild) {
      activeRoute = activeRoute.firstChild
    }

    return (
      (activeRoute.data[COMPANY_INFO_DATA_TAB_NAME] as ECompanyInfoRoute) ?? ECompanyInfoRoute.EARN
    )
  }

  private getReportHeadline(summary: string): string | undefined {
    const report = this.document.createElement('div')

    report.innerHTML = summary

    return report.querySelector('h1')?.textContent?.trim() || undefined
  }

  private getIsoDate(value: string | null | undefined): string | undefined {
    if (!value) {
      return undefined
    }

    const timestamp = Date.parse(value)

    return Number.isNaN(timestamp) ? undefined : new Date(timestamp).toISOString()
  }
}
